import { Link } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import { CATEGORY_VIDEOS } from '@/config/mediaConfig';

const CATEGORIES = [
  {
    name: 'MODULAR WORKSTATIONS',
    description: 'Transform your office with ergonomic modular desks',
    image: 'https://customer-assets.emergentagent.com/job_modulify/artifacts/mnuk1yqr_Gemini_Generated_Image_bi5of5bi5of5bi5o.png',
    link: '/products?category=Modular Office Workstation',
    videoKey: 'Modular Office Workstation'
  },
  {
    name: 'STORAGE SOLUTIONS',
    description: 'Organize your workspace with smart storage',
    image: 'https://customer-assets.emergentagent.com/job_modulify/artifacts/6knuukw8_Gemini_Generated_Image_i1243oi1243oi124.png',
    link: '/products?category=Storage Cabinets',
    videoKey: 'Storage Cabinets'
  },
  {
    name: 'PRIVACY BOOTHS',
    description: 'Create focused spaces for calls and meetings',
    image: 'https://customer-assets.emergentagent.com/job_modulify/artifacts/54ig9j49_Gemini_Generated_Image_himmkrhimmkrhimm.png',
    link: '/products?category=Privacy Booths',
    videoKey: 'Privacy Booths'
  }
];

export function CategoriesSection() {
  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-black uppercase mb-4 text-foreground">
            BUY OFFICE FURNITURE ONLINE
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore our range of innovative furniture solutions designed for modern workspaces. Buy office furniture online with easy ordering and pan-India delivery.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {CATEGORIES.map((category, index) => (
            <Link
              key={index}
              to={category.link}
              className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105"
            >
              <div className="aspect-[4/3] overflow-hidden">
                {CATEGORY_VIDEOS[category.videoKey] ? (
                  <video
                    src={CATEGORY_VIDEOS[category.videoKey]}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                ) : (
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
              </div>
              
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-2xl font-black uppercase mb-2 group-hover:text-primary transition-colors">
                  {category.name}
                </h3>
                <p className="text-sm text-white/90 mb-4">
                  {category.description}
                </p>
                <div className="flex items-center gap-2 text-primary font-bold">
                  <span>SHOP NOW</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

const VIDEOS = [
  { 
    video: 'https://customer-assets.emergentagent.com/job_ece35ff3-b481-4bf6-86eb-5c3e446db369/artifacts/ghdgr0eh_WhatsApp%20Video%202026-02-18%20at%2013.11.08.mp4',
    title: 'Workstation Setup',
    link: '/products?category=Modular Office Workstation'
  },
  { 
    video: 'https://customer-assets.emergentagent.com/job_ece35ff3-b481-4bf6-86eb-5c3e446db369/artifacts/nmfggj36_WhatsApp%20Video%202026-02-18%20at%2013.11.32.mp4',
    title: 'Office Tour',
    link: '/products'
  },
  { 
    video: 'https://customer-assets.emergentagent.com/job_ece35ff3-b481-4bf6-86eb-5c3e446db369/artifacts/y2b9fq8x_WhatsApp%20Video%202026-02-18%20at%2013.11.42.mp4',
    title: 'Privacy Booth Demo',
    link: '/products?category=Privacy Booths'
  },
  { 
    video: 'https://customer-assets.emergentagent.com/job_ece35ff3-b481-4bf6-86eb-5c3e446db369/artifacts/taorhb4q_WhatsApp%20Video%202026-02-18%20at%2013.11.421.mp4',
    title: 'Storage Solutions',
    link: '/products?category=Storage Cabinets'
  }
];

export function VideoShowcaseClassic() {
  const handleVideoClick = (e) => {
    const video = e.currentTarget.querySelector('video');
    if (video) {
      if (video.muted) {
        video.muted = false;
        video.currentTime = 0;
        video.play();
        e.currentTarget.querySelector('.play-indicator')?.classList.add('hidden');
        e.currentTarget.querySelector('.sound-indicator')?.classList.remove('hidden');
      } else {
        video.muted = true;
        e.currentTarget.querySelector('.play-indicator')?.classList.remove('hidden');
        e.currentTarget.querySelector('.sound-indicator')?.classList.add('hidden');
      }
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-8">
        <div className="text-center mb-12">
          <span className="bg-primary/10 text-primary px-4 py-1 rounded-full text-sm font-bold uppercase mb-4 inline-block">
            See It In Action
          </span>
          <h2 className="text-4xl md:text-5xl font-black uppercase mb-4">
            Watch<span className="text-primary">.</span> Explore<span className="text-primary">.</span> Furnish<span className="text-primary">.</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            See our modular furniture in action. Click any video to watch with sound.
          </p>
        </div>

        {/* Video Carousel */}
        <div className="relative max-w-6xl mx-auto">
          <div className="flex gap-4 overflow-x-auto pb-6 snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {VIDEOS.map((item, i) => (
              <div 
                key={i}
                className="flex-shrink-0 w-[280px] snap-start group cursor-pointer"
                onClick={handleVideoClick}
              >
                <div className="relative h-[420px] rounded-xl overflow-hidden bg-gray-900 shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] border-2 border-transparent hover:border-primary">
                  <video
                    src={item.video}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white font-bold text-lg">{item.title}</p>
                    <span className="text-primary text-sm flex items-center gap-1">
                      Tap to play with sound
                    </span>
                  </div>
                  
                  <div className="play-indicator absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-primary/80 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/50">
                      <Play className="w-8 h-8 text-white fill-white ml-1" />
                    </div>
                  </div>
                  
                  <div className="sound-indicator hidden absolute top-4 right-4">
                    <div className="bg-primary text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      🔊 Playing
                    </div>
                  </div>
                </div>
                
                <Link 
                  to={item.link}
                  className="mt-3 flex items-center justify-center gap-2 text-foreground hover:text-primary font-medium transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  Shop {item.title} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-2 mt-4">
            <span className="text-sm text-muted-foreground">← Scroll to explore →</span>
          </div>
        </div>
      </div>
    </section>
  );
}
